import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api, apiError } from "@/lib/api";
import { RESOURCE_GROUP, PII_RESOURCES, PLATFORM_RESOURCES } from "@/lib/rbac";
// MODO DEMO (sep-2026): módulo inerte en producción (DEMO_ACTIVO === false).
import { DEMO_ACTIVO, SESION_DEMO } from "@/demo/modo";

const AuthContext = createContext(null);

// ─── Cierre de sesión por inactividad (feb-2026) ───────────────────────────
// Umbral, ventana de aviso previo y eventos considerados como "actividad real"
// del usuario. Ajústalos aquí, en un solo lugar, sin buscar en el archivo.
// El JWT del backend sigue durando 7 días (server.py:128) — este candado es
// puramente de frontend: cierra la sesión antes de que expire el token si no
// hay actividad, y le da al usuario un aviso con cuenta regresiva antes.
const INACTIVIDAD_UMBRAL_MS = 10 * 60 * 1000; // 10 min → cierre automático
const INACTIVIDAD_AVISO_MS  =  9 * 60 * 1000; //  9 min → aviso previo (1 min antes del cierre)
const INACTIVIDAD_COUNTDOWN_SEG = 60;         // segundos entre aviso y cierre efectivo
const INACTIVIDAD_EVENTOS = ["mousemove", "mousedown", "keydown", "scroll", "touchstart"];
// Throttle mínimo para re-armar el temporizador: no lo hacemos en cada mousemove
// (excesivo). 2s es suficiente para percibirse instantáneo sin overhead.
const INACTIVIDAD_THROTTLE_MS = 2000;
// (INACTIVIDAD_UMBRAL_MS - INACTIVIDAD_AVISO_MS) === INACTIVIDAD_COUNTDOWN_SEG*1000
// Invariante documental: el aviso siempre precede al cierre por exactamente 60s.

export function AuthProvider({ children }) {
  // En MODO DEMO la sesión arranca YA resuelta (sin red, sin login obligatorio): cualquier
  // ruta interna abre directo. En producción el arranque es el de siempre.
  const [user, setUser] = useState(DEMO_ACTIVO ? SESION_DEMO.user : null); // null = checking, false = anon, object = auth
  const [meta, setMeta] = useState(DEMO_ACTIVO ? SESION_DEMO.meta : null);
  const [perms, setPerms] = useState(DEMO_ACTIVO ? SESION_DEMO.perms : null); // { role, es_plataforma, permisos, pii_bloqueado }

  // ─── Estado del detector de inactividad ────────────────────────────────
  // avisoAbierto se refleja también en `avisoAbiertoRef` para poder leerlo
  // desde el handler de eventos sin re-registrar listeners en cada cambio.
  const [avisoAbierto, setAvisoAbierto] = useState(false);
  const [countdown, setCountdown] = useState(INACTIVIDAD_COUNTDOWN_SEG);
  const avisoAbiertoRef = useRef(false);
  // Ref con la versión más reciente de `logout` para que el efecto de inactividad
  // NO tenga que listarlo como dependencia (si lo hiciera, el efecto se re-armaría
  // en cada render y los timers se reiniciarían solos, invalidando la cuenta).
  const logoutRef = useRef(null);
  // Refs para exponer los handlers al Dialog (definido fuera del useEffect).
  const continuarSesionRef = useRef(() => {});
  const cerrarPorInactividadRef = useRef(async () => {});

  const navigate = useNavigate();

  const loadMeta = useCallback(async () => {
    try {
      const { data } = await api.get("/meta");
      setMeta(data);
    } catch (e) {
      console.debug("No se pudo cargar /meta:", e?.message || e);
    }
  }, []);

  const loadPerms = useCallback(async () => {
    try {
      const { data } = await api.get("/rbac/me");
      // Mesa de Control para el ejecutivo solo con el interruptor de la entidad encendido
      // (visual; el candado real está en desembolso.py).
      if (data?.role === "ejecutivo_credito") {
        let habilitado = false;
        try { habilitado = !!(await api.get("/desembolso-originador/config")).data?.habilitado; } catch (_e) { /* apagado */ }
        const sinMesa = (data.areas_acceso || []).filter((a) => a !== "mesa_control");
        data.areas_acceso = habilitado ? [...sinMesa, "mesa_control"] : sinMesa;
      }
      setPerms(data);
    } catch (e) {
      setPerms(null);
    }
  }, []);

  useEffect(() => {
    if (DEMO_ACTIVO) return; // sesión ficticia ya cargada en el estado inicial
    const token = localStorage.getItem("cos_token");
    if (!token) {
      setUser(false);
      loadMeta();
      return;
    }
    api
      .get("/auth/me")
      .then((r) => {
        setUser(r.data);
        loadPerms();
      })
      .catch(() => {
        localStorage.removeItem("cos_token");
        setUser(false);
      })
      .finally(loadMeta);
  }, [loadMeta, loadPerms]);

  const login = async (email, password, cfTurnstileToken) => {
    // DEMO: el botón "Entrar" nunca falla y no sale a la red.
    if (DEMO_ACTIVO) {
      try { localStorage.setItem("cos_token", SESION_DEMO.token); } catch (_e) { /* modo privado */ }
      setUser(SESION_DEMO.user);
      setPerms(SESION_DEMO.perms);
      setMeta(SESION_DEMO.meta);
      return { ok: true };
    }
    try {
      const { data } = await api.post("/auth/login", { email, password, cf_turnstile_token: cfTurnstileToken });
      // 2FA Fase 5 (feb-2026): si el backend responde con reto MFA (requires_mfa),
      // NO establecemos sesión — devolvemos el challenge al llamador para que muestre
      // la pantalla del código. El challenge vive SOLO en memoria (state React), muere al recargar.
      if (data.requires_mfa) {
        return {
          ok: false, mfa: true,
          challenge: data.mfa_challenge_token,
          email_masked: data.email_masked,
          mfa_send_failed: !!data.mfa_send_failed,
          expira: data.expira || "10 minutos",
          // Códigos de respaldo (Bloque 2): solo el super_admin los tiene.
          backup_disponible: !!data.backup_disponible,
          backup_restantes: data.backup_restantes ?? 0,
          backup_aviso_bajo: !!data.backup_aviso_bajo,
        };
      }
      localStorage.setItem("cos_token", data.access_token);
      setUser(data.user);
      loadPerms();
      return { ok: true };
    } catch (e) {
      // Detecta el gate de correo no verificado (backend responde 403 con detail.codigo).
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.codigo === "email_no_verificado") {
        return { ok: false, error: detail.mensaje, codigo: "email_no_verificado", email: detail.email };
      }
      return { ok: false, error: apiError(e) };
    }
  };

  // 2FA Fase 5 (feb-2026): canjea el challenge + código por un access_token real.
  // Reutiliza el shape idéntico al de /auth/login (Fase 2). Al éxito, establece sesión
  // exactamente como el login normal (mismo setUser + loadPerms + token en localStorage).
  const loginMfaVerify = async (challenge, codigo) => {
    try {
      const { data } = await api.post("/auth/login-mfa/verify",
                                       { mfa_challenge_token: challenge, codigo });
      localStorage.setItem("cos_token", data.access_token);
      setUser(data.user);
      loadPerms();
      return { ok: true };
    } catch (e) {
      // Diferenciar razones para que la UI pueda decidir si vuelve al paso 1
      const st = e?.response?.status;
      const detail = e?.response?.data?.detail || "";
      // Solo el CHALLENGE expirado (JWT vencido) obliga a volver al paso 1 — el mensaje
      // del backend es "Challenge MFA expirado; vuelve a iniciar sesión." (Fase 2).
      // El código incorrecto/ya-usado devuelve "Código MFA inválido o expirado." → NO regresa,
      // solo muestra el error y permite reintentar.
      const expirado = typeof detail === "string" && /challenge/i.test(detail);
      const limite = st === 429;
      return { ok: false, status: st, expirado, limite, error: apiError(e) };
    }
  };

  const loginMfaResend = async (challenge) => {
    try {
      const { data } = await api.post("/auth/login-mfa/request",
                                       { mfa_challenge_token: challenge });
      return { ok: true, codigo: data.codigo || "", mensaje: data.mensaje || "",
               envio_aceptado: !!data.envio_aceptado, mfa_send_failed: !!data.mfa_send_failed,
               reintentar_en: data.reintentar_en ?? 0,
               espera_total: data.espera_total ?? 60,
               backup_disponible: !!data.backup_disponible,
               backup_restantes: data.backup_restantes ?? 0 };
    } catch (e) {
      const st = e?.response?.status;
      return { ok: false, status: st, limite: st === 429, error: apiError(e) };
    }
  };

  // 2FA · Bloque 2 (ago-2026): canje con CÓDIGO DE RESPALDO. Puerta de emergencia del
  // super_admin cuando el correo del código no llega. Mismo efecto que `loginMfaVerify`:
  // al éxito establece sesión igual que el login normal.
  const loginMfaVerifyBackup = async (challenge, codigo) => {
    try {
      const { data } = await api.post("/auth/login-mfa/verify-backup",
                                       { mfa_challenge_token: challenge, codigo });
      localStorage.setItem("cos_token", data.access_token);
      setUser(data.user);
      loadPerms();
      return { ok: true, backup_restantes: data.backup_restantes,
               backup_aviso_bajo: !!data.backup_aviso_bajo };
    } catch (e) {
      const st = e?.response?.status;
      const detail = e?.response?.data?.detail || "";
      const expirado = typeof detail === "string" && /challenge/i.test(detail);
      return { ok: false, status: st, expirado, limite: st === 429, error: apiError(e) };
    }
  };

  const registerEntity = async (payload, csfFile) => {
    // feb 2026 iter 07-24l — El registro YA NO devuelve access_token. El usuario debe
    // verificar su correo (clic en el enlace enviado por Brevo) antes de poder iniciar
    // sesión. Antes bypasseábamos ese candado emitiendo token en el registro y varando a
    // los usuarios en el siguiente login. Devolvemos `email` y `entity_estatus` para que
    // `RegisterWizard` redirija a `/verificar-correo-pendiente?email=...`.
    try {
      const fd = new FormData();
      fd.append("payload_json", JSON.stringify(payload));
      if (csfFile) fd.append("csf", csfFile, csfFile.name);
      const { data } = await api.post("/auth/register-entity", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return {
        ok: true,
        email: data.email,
        entity_estatus: data.entity_estatus,
        entity_nombre: data.entity_nombre,
        // ago-2026 · verdad sobre el correo: si Brevo no aceptó el envío, la pantalla
        // siguiente lo dice en vez de prometer un enlace que nunca salió.
        verificacion_enviada: data.verificacion_enviada,
        mensaje: data.mensaje,
      };
    } catch (e) {
      return { ok: false, error: apiError(e) };
    }
  };

  const logout = async () => {
    // DEMO: no existe pantalla de acceso y la sesión ficticia NO se destruye —
    // ninguna ruta interna puede quedar cerrada en una demostración.
    if (DEMO_ACTIVO) {
      toast.info("Entorno de demostración", {
        id: "demo-sesion-permanente",
        description: "La sesión permanece abierta: la demostración no tiene pantalla de acceso.",
      });
      navigate("/dashboard");
      return;
    }
    try {
      await api.post("/auth/logout");
    } catch (e) {
      console.debug("Logout best-effort falló:", e?.message || e);
    }
    localStorage.removeItem("cos_token");
    setUser(false);
    setPerms(null);
  };

  // ─── Sync de refs para el efecto de inactividad ────────────────────────
  // `logoutRef.current` siempre apunta a la definición más reciente de logout,
  // evitando pasarlo como dependencia del useEffect (que reiniciaría timers).
  logoutRef.current = logout;
  // `avisoAbiertoRef` refleja el state para lecturas dentro del handler global
  // de actividad, sin re-registrar el listener cuando el modal se abre/cierra.
  useEffect(() => { avisoAbiertoRef.current = avisoAbierto; }, [avisoAbierto]);

  // ─── Detector de inactividad (feb-2026) ────────────────────────────────
  // Solo opera cuando hay usuario autenticado (`user` es un objeto). Con
  // `user === false` (anon) o `user === null` (checking), no hace nada.
  //
  // Flujo:
  //   t=0            → armamos timers al montar / tras cada actividad
  //   t=9 min        → mostramos modal con cuenta regresiva de 60s
  //   t=10 min       → llamamos logout() y navegamos a /login con toast
  // La actividad del usuario NO reinicia el timer mientras el aviso está
  // abierto — el usuario debe presionar "Continuar sesión" explícitamente,
  // política intencional para que un movimiento accidental del mouse en un
  // equipo desatendido no reanude la sesión.
  useEffect(() => {
    if (!user) return; // sin sesión → sin timers, sin listeners
    if (DEMO_ACTIVO) return; // DEMO: el cierre por inactividad queda desactivado

    let timerAviso = null;
    let timerCierre = null;
    let timerCountdown = null;
    let lastReset = 0;

    const clearAll = () => {
      if (timerAviso) { clearTimeout(timerAviso); timerAviso = null; }
      if (timerCierre) { clearTimeout(timerCierre); timerCierre = null; }
      if (timerCountdown) { clearInterval(timerCountdown); timerCountdown = null; }
    };

    const cerrarPorInactividad = async () => {
      clearAll();
      setAvisoAbierto(false);
      setCountdown(INACTIVIDAD_COUNTDOWN_SEG);
      // Reutiliza el logout ya existente: llama POST /auth/logout (bitácora),
      // borra "cos_token" de localStorage, y hace setUser(false)+setPerms(null).
      try { await logoutRef.current(); } catch (_) { /* ya loggeado internamente */ }
      // Mensaje visible al usuario en la pantalla de login (Toaster global en App.js).
      toast.warning(
        "La sesión se cerró por inactividad. Por seguridad, vuelve a iniciar sesión.",
        { duration: 8000, id: "logout-inactividad" }
      );
      navigate("/login", { replace: true });
    };

    const armarTimers = () => {
      clearAll();
      timerAviso = setTimeout(() => {
        // Al minuto 9: abrir modal + iniciar countdown visual.
        setAvisoAbierto(true);
        setCountdown(INACTIVIDAD_COUNTDOWN_SEG);
        timerCountdown = setInterval(() => {
          setCountdown((c) => (c > 0 ? c - 1 : 0));
        }, 1000);
        // Al segundo 60 del countdown (== minuto 10 total): cierre efectivo.
        timerCierre = setTimeout(cerrarPorInactividad, INACTIVIDAD_COUNTDOWN_SEG * 1000);
      }, INACTIVIDAD_AVISO_MS);
    };

    const onActivity = () => {
      // Mientras el aviso está abierto, la actividad NO reinicia — se requiere
      // clic explícito en el botón "Continuar sesión".
      if (avisoAbiertoRef.current) return;
      const now = Date.now();
      if (now - lastReset < INACTIVIDAD_THROTTLE_MS) return;
      lastReset = now;
      armarTimers();
    };

    // Handlers expuestos al Dialog vía refs (fuera del useEffect scope).
    continuarSesionRef.current = () => {
      setAvisoAbierto(false);
      setCountdown(INACTIVIDAD_COUNTDOWN_SEG);
      armarTimers();
    };
    cerrarPorInactividadRef.current = cerrarPorInactividad;

    // Registro de escuchadores (passive: true → no bloquean scroll/gestos).
    for (const ev of INACTIVIDAD_EVENTOS) {
      window.addEventListener(ev, onActivity, { passive: true });
    }
    armarTimers();

    // Limpieza — TAREA 4. Se ejecuta al desmontarse el provider, al hacer logout
    // (user pasa a false → efecto se re-corre y desmonta el anterior) y al
    // navegar fuera. Deja el árbol sin timers colgantes.
    return () => {
      for (const ev of INACTIVIDAD_EVENTOS) {
        window.removeEventListener(ev, onActivity);
      }
      clearAll();
      continuarSesionRef.current = () => {};
      cerrarPorInactividadRef.current = async () => {};
    };
  }, [user, navigate]);

  // Permiso efectivo del usuario actual sobre un recurso (espejo de backend authorize)
  const can = useCallback(
    (resource, action) => {
      if (!user) return false;
      const group = RESOURCE_GROUP[resource] || "otros";
      if (user.role === "super_admin") {
        if (PII_RESOURCES.has(resource)) return false;
        if (action === "ver" || action === "descargar") return true;
        return PLATFORM_RESOURCES.has(resource);
      }
      // Garantías: crear/editar/cancelar SOLO admin de entidad. El resto (área Cartera) solo lectura.
      if (resource === "garantias" && (action === "crear" || action === "editar" || action === "eliminar")) {
        return perms?.es_admin_total === true;
      }
      if (action === "ver" || action === "descargar") return true;
      if (!perms) return false;
      const allowed = perms.permisos?.[group] || [];
      return allowed.includes(action);
    },
    [user, perms]
  );

  // ¿El usuario tiene acceso al ÁREA del menú? (ocultamiento real, no cosmético)
  // R7 · el backend YA filtra `areas_acceso` por giro (SOFOM vs Inmobiliaria) y por rol
  // (super_admin recibe SOLO áreas de plataforma). Respetamos ese cálculo para TODOS los
  // roles — incluyendo super_admin — para eliminar la fuga de menús inmobiliarios que
  // existía cuando el frontend bypaseaba con `return true` para super_admin.
  const hasArea = useCallback(
    (area) => {
      if (!area || area === "principal" || area === "plataforma" || area === "otros") return true;
      if (!user) return false;
      if (!perms) return true; // mientras carga, no ocultar (evita parpadeo del menú)
      return (perms.areas_acceso || []).includes(area);
    },
    [user, perms]
  );

  // ¿El módulo/función está BLOQUEADO por el plan contratado? (candado de plan, no de rol)
  // NO se bypassa por es_admin_total: el plan limita a toda la entidad, incluido el admin.
  const moduloBloqueado = useCallback(
    (feature) => {
      if (!feature || !perms) return false;
      return (perms.modulos_bloqueados || []).includes(feature);
    },
    [perms]
  );

  return (
    <AuthContext.Provider value={{ user, meta, perms, giro: perms?.giro || "sofom", can, hasArea, moduloBloqueado, login, loginMfaVerify, loginMfaVerifyBackup, loginMfaResend, registerEntity, logout }}>
      {children}
      {/* Modal de aviso previo por inactividad (feb-2026).
          Se abre a los 9 minutos sin actividad y cuenta hacia atrás 60 segundos.
          - "Continuar sesión" → reset del temporizador, sesión sigue viva.
          - Cerrar el modal (X o fuera) → equivale a "Continuar sesión".
          - Sin respuesta 60s → cierre efectivo vía logout(). */}
      <Dialog open={avisoAbierto} onOpenChange={(open) => {
        // Cualquier gesto que intente cerrar el modal (X, tecla ESC, click fuera)
        // se interpreta como "Continuar sesión", ya que el usuario está presente.
        if (!open && avisoAbierto) continuarSesionRef.current();
      }}>
        <DialogContent data-testid="dialog-inactividad" className="max-w-md">
          <DialogHeader>
            <DialogTitle>¿Sigues ahí?</DialogTitle>
            <DialogDescription>
              Por seguridad, tu sesión se cerrará por inactividad en
              <span data-testid="inactividad-countdown"
                    className="mx-1 font-semibold text-forest-700">
                {countdown}
              </span>
              segundos. Presiona <strong>Continuar sesión</strong> para seguir trabajando.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-2">
            <Button
              data-testid="btn-continuar-sesion"
              onClick={() => continuarSesionRef.current()}
              className="w-full sm:w-auto">
              Continuar sesión
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
